import { useState, useEffect } from 'react';
import WebsiteLayout from '@/components/layout/WebsiteLayout';
import { Badge } from '@/components/ui/badge';
import { Building2, Globe, Users, Award, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

// Official client list with logo-optimized domains
const allClients = [
  // Banks & Financial Institutions
  { name: 'Public Bank', domain: 'pbebank.com', industry: 'Banking' },
  { name: 'Maybank', domain: 'maybank.com', industry: 'Banking' },
  { name: 'Bank Rakyat', domain: 'bankrakyat.com.my', industry: 'Banking' },
  { name: 'Standard Chartered', domain: 'sc.com', industry: 'Banking' },
  { name: 'RHB Bank', domain: 'rhbgroup.com', industry: 'Banking' },
  { name: 'Alliance Bank', domain: 'alliancebank.com.my', industry: 'Banking' },
  { name: 'Bursa Malaysia', domain: 'bursamalaysia.com', industry: 'Financial Services' },
  
  // Insurance & Takaful
  { name: 'AIA Malaysia', domain: 'aia.com.my', industry: 'Insurance' },
  { name: 'AIA General', domain: 'aia.com.my', industry: 'Insurance' },
  { name: 'Great Eastern', domain: 'greateasternlife.com', industry: 'Insurance' },
  { name: 'Great Eastern Takaful', domain: 'greateasterntakaful.com', industry: 'Takaful' },
  { name: 'Prudential', domain: 'prudential.com.my', industry: 'Insurance' },
  { name: 'Prudential BSN', domain: 'prudential.com', industry: 'Insurance' },
  { name: 'MetLife', domain: 'metlife.com', industry: 'Insurance' },
  { name: 'Sun Life Malaysia', domain: 'sunlife.com', industry: 'Insurance' },
  { name: 'AXA Malaysia', domain: 'axa.com', industry: 'Insurance' },
  { name: 'FWD Insurance', domain: 'fwd.com', industry: 'Insurance' },
  { name: 'Pacific Insurance', domain: 'pacificinsurance.com.my', industry: 'Insurance' },
  { name: 'Gibraltar BSN', domain: 'gibraltar-bsn.com.my', industry: 'Insurance' },
  { name: 'Takaful Malaysia', domain: 'takaful-malaysia.com.my', industry: 'Takaful' },
  { name: 'Takaful Ikhlas', domain: 'takaful-ikhlas.com.my', industry: 'Takaful' },
  { name: 'Syarikat Takaful', domain: 'takaful-malaysia.com.my', industry: 'Takaful' },
  { name: 'MLRe Malaysian Life', domain: 'mlre.com.my', industry: 'Reinsurance' },
  { name: 'Tune Protect', domain: 'tuneprotect.com', industry: 'Insurance' },
  
  // Telecommunications & Technology
  { name: 'TM', domain: 'tm.com.my', industry: 'Telecommunications' },
  { name: 'Astro', domain: 'astro.com.my', industry: 'Media & Entertainment' },
  { name: 'Naza Communications', domain: 'naza.com.my', industry: 'Telecommunications' },
  { name: 'LTIMindtree', domain: 'ltimindtree.com', industry: 'IT Services' },
  { name: 'MolPay', domain: 'razer.com', industry: 'Fintech' },
  
  // Conglomerates & Corporations
  { name: 'YTL', domain: 'ytl.com', industry: 'Conglomerate' },
  { name: 'Sime Darby', domain: 'simedarby.com', industry: 'Conglomerate' },
  { name: 'PPB Group', domain: 'ppbgroup.com', industry: 'Conglomerate' },
  { name: 'AAM Group', domain: 'aam.com.my', industry: 'Automotive' },
  
  // Aviation & Logistics
  { name: 'Malaysia Airlines', domain: 'malaysiaairlines.com', industry: 'Aviation' },
  { name: 'DHL', domain: 'dhl.com', industry: 'Logistics' },
  { name: 'POS Malaysia', domain: 'pos.com.my', industry: 'Logistics' },
  
  // Energy & Industrial
  { name: 'Orsted', domain: 'orsted.com', industry: 'Energy' },
  { name: 'Forbes Marshall', domain: 'forbesmarshall.com', industry: 'Engineering' },
  { name: 'MS First Capital', domain: 'msfirstcapital.com', industry: 'Financial Services' },
  { name: 'Quality Kiosk', domain: 'qualitykiosk.com', industry: 'Technology' },
];

// Get unique industries
const industries = ['All', ...new Set(allClients.map(c => c.industry))];

// Logo.dev API token (free tier)
const LOGO_DEV_TOKEN = 'pk_X-1ZO13GSgeOoUrIuJ6GMQ';

// Client Card Component with logo.dev API
const ClientCard = ({ client }) => {
  const [logoError, setLogoError] = useState(false);
  
  // Use logo.dev as primary source (most reliable for company logos)
  const logoUrl = `https://img.logo.dev/${client.domain}?token=${LOGO_DEV_TOKEN}&size=128&format=png`;
  
  // Generate a consistent color based on company name
  const getColorClass = (name) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-purple-500 to-purple-600',
      'from-green-500 to-green-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600',
      'from-cyan-500 to-cyan-600',
      'from-indigo-500 to-indigo-600',
      'from-rose-500 to-rose-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };
  
  return (
    <div
      className="group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col items-center justify-center hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all hover:scale-105"
      data-testid={`client-${client.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-16 h-16 flex items-center justify-center mb-3">
        {!logoError ? (
          <img
            src={logoUrl}
            alt={client.name}
            className="max-w-full max-h-full object-contain"
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className={`w-14 h-14 bg-gradient-to-br ${getColorClass(client.name)} rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md`}>
            {client.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
          </div>
        )}
      </div>
      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 text-center leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400">
        {client.name}
      </p>
      <Badge variant="secondary" className="mt-2 text-[10px] px-2 py-0">
        {client.industry}
      </Badge>
    </div>
  );
};

export default function ClientsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [filteredClients, setFilteredClients] = useState(allClients);

  useEffect(() => {
    let filtered = allClients;
    
    if (selectedIndustry !== 'All') {
      filtered = filtered.filter(c => c.industry === selectedIndustry);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.industry.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredClients(filtered);
  }, [searchTerm, selectedIndustry]);

  // Stats
  const stats = [
    { label: 'Enterprise Clients', value: '50+', icon: Building2 },
    { label: 'Industries Served', value: industries.length - 1, icon: Globe },
    { label: 'Years of Trust', value: '8+', icon: Award },
    { label: 'Projects Delivered', value: '200+', icon: Users },
  ];

  return (
    <WebsiteLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>
        {/* Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1763739530672-4aadafbd81ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBtZWV0aW5nJTIwcm9vbSUyMHByZXNlbnRhdGlvbiUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8fDE3Nzk3ODE2MTN8MA&ixlib=rb-4.1.0&q=85" 
            alt="Client meeting"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge className="mb-4 bg-blue-500/20 text-blue-300 border-blue-500/30">
                Trusted Partnerships
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Our Valued <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Clients</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto lg:mx-0">
                Empowering Malaysia's leading enterprises with AI-powered IT solutions across banking, insurance, telecommunications, and more.
              </p>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1769739576456-0aefcff3f4b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtZWV0aW5nJTIwcm9vbSUyMHByZXNlbnRhdGlvbiUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8fDE3Nzk3ODE2MTN8MA&ixlib=rb-4.1.0&q=85" 
                alt="Team meeting"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                <stat.icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-slate-50 dark:bg-slate-900 py-8 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="client-search"
              />
            </div>
            
            <div className="flex flex-wrap gap-2 justify-center">
              {industries.map((industry) => (
                <button
                  key={industry}
                  onClick={() => setSelectedIndustry(industry)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedIndustry === industry
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                  }`}
                  data-testid={`filter-${industry.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>
          
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 text-center">
            Showing {filteredClients.length} of {allClients.length} clients
          </p>
        </div>
      </section>

      {/* Clients Grid */}
      <section className="py-16 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredClients.map((client, index) => (
              <ClientCard key={`${client.name}-${index}`} client={client} />
            ))}
          </div>

          {filteredClients.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <p className="text-slate-500 dark:text-slate-400">No clients found matching your criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Join Our Client Portfolio?
          </h2>
          <p className="text-blue-100 mb-8">
            Partner with us to transform your business with cutting-edge technology solutions
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-colors"
            data-testid="contact-cta"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </WebsiteLayout>
  );
}
